<template>
    <a-modal
        :title="$t('chatbotv2.exportDiagram')"
        :visible="visible"
        @cancel="$emit('close')"
        :footer="null"
        :width="700"
        wrapClassName="export-diagram-modal"
    >
        <div class="export-container">
            <div class="export-description">
                {{ $t('chatbotv2.exportDiagramDescription') }}
            </div>

            <a-spin :spinning="loading">
                <!-- Diagram Section -->
                <div class="export-section">
                    <div class="section-header">
                        <span class="section-title">{{ $t('chatbotv2.diagram') }}</span>
                        <a-switch :checked="true" disabled />
                    </div>
                    <div class="section-description">
                        {{ $t('chatbotv2.diagramExportDesc') }}
                    </div>
                </div>

                <!-- AI Settings Section -->
                <div class="export-section">
                    <div class="section-header">
                        <span class="section-title">{{ $t('chatbotv2.aiSettings') }}</span>
                        <a-switch v-model="exportOptions.aiSettings" :disabled="!hasAiSettings" />
                    </div>
                    <div class="section-description">
                        {{ $t('chatbotv2.aiSettingsExportDesc') }}
                    </div>
                    <div class="ai-settings-details" v-if="exportOptions.aiSettings && hasAiSettings">
                        <div class="detail-item">
                            <span class="label">{{ $t('chatbotv2.model') }}:</span>
                            <span class="value">{{ storyAi.model || 'gpt-4.1' }}</span>
                        </div>
                        <div class="detail-item">
                            <span class="label">{{ $t('chatbotv2.temperature') }}:</span>
                            <span class="value">{{ storyAi.temperature || 0.7 }}</span>
                        </div>
                        <div class="detail-item">
                            <span class="label">{{ $t('chatbotv2.topP') }}:</span>
                            <span class="value">{{ storyAi.top_p || 1 }}</span>
                        </div>
                        <div class="detail-item">
                            <span class="label">{{ $t('chatbotv2.toolChoice') }}:</span>
                            <span class="value">{{ storyAi.tool_choice || 'auto' }}</span>
                        </div>
                        <div class="detail-item" v-if="storyAi.prompt">
                            <span class="label">{{ $t('chatbotv2.prompt') }}:</span>
                            <span class="value truncate">{{ storyAi.prompt.substring(0, 100) }}...</span>
                        </div>
                    </div>
                </div>

                <!-- AI Functions Section -->
                <div class="export-section" v-if="kbCounts.aiFunction > 0">
                    <div class="section-header">
                        <span class="section-title">
                            {{ $t('chatbotv2.aiFunctions') }}
                            <span class="count-badge">{{ kbCounts.aiFunction }}</span>
                        </span>
                        <a-switch v-model="exportOptions.aiFunctions" />
                    </div>
                    <div class="section-description">
                        {{ $t('chatbotv2.aiFunctionsExportDesc') }}
                    </div>
                    <div class="function-list" v-if="exportOptions.aiFunctions && aiFunctionsList.length > 0">
                        <div class="select-all-row">
                            <a-checkbox 
                                :checked="isAllFunctionsSelected" 
                                :indeterminate="isSomeFunctionsSelected && !isAllFunctionsSelected"
                                @change="toggleAllFunctions"
                            >
                                {{ $t('chatbotv2.selectAll') }}
                            </a-checkbox>
                        </div>
                        <div class="function-item" v-for="fn in aiFunctionsList" :key="fn._id">
                            <a-checkbox 
                                :checked="selectedFunctions.includes(fn._id)" 
                                @change="(e) => toggleFunction(fn._id, e.target.checked)"
                            >
                                {{ fn.aiFunction.name }}
                            </a-checkbox>
                        </div>
                    </div>
                </div>

                <!-- Knowledge Base Section -->
                <div class="export-section">
                    <div class="section-header">
                        <span class="section-title">{{ $t('chatbotv2.knowledgeBase') }}</span>
                    </div>
                    <div class="section-description">
                        {{ $t('chatbotv2.knowledgeBaseExportDesc') }}
                    </div>
                    <div class="kb-options">
                        <div class="kb-option" v-if="kbCounts.faq > 0">
                            <div class="kb-option-info">
                                <span class="kb-name">FAQ</span>
                                <span class="kb-count">{{ kbCounts.faq }} {{ $t('message.items') }}</span>
                            </div>
                            <a-switch v-model="exportOptions.kb.faq" />
                        </div>
                        <div class="kb-option" v-if="kbCounts.file > 0">
                            <div class="kb-option-info">
                                <span class="kb-name">{{ $t('chatbotv2.files') }}</span>
                                <span class="kb-count">{{ kbCounts.file }} {{ $t('message.items') }}</span>
                            </div>
                            <a-switch v-model="exportOptions.kb.file" />
                        </div>
                        <div class="kb-option" v-if="kbCounts.article > 0">
                            <div class="kb-option-info">
                                <span class="kb-name">{{ $t('chatbotv2.articles') }}</span>
                                <span class="kb-count">{{ kbCounts.article }} {{ $t('message.items') }}</span>
                            </div>
                            <a-switch v-model="exportOptions.kb.article" />
                        </div>
                        <div class="kb-option" v-if="kbCounts.webpage > 0">
                            <div class="kb-option-info">
                                <span class="kb-name">{{ $t('chatbotv2.webPages') }}</span>
                                <span class="kb-count">{{ kbCounts.webpage }} {{ $t('message.items') }}</span>
                            </div>
                            <a-switch v-model="exportOptions.kb.webpage" />
                        </div>
                        <div class="no-kb-message" v-if="totalKbCount === 0">
                            {{ $t('chatbotv2.noKnowledgeBase') }}
                        </div>
                    </div>
                </div>
            </a-spin>

            <!-- Actions -->
            <div class="export-actions">
                <a-button @click="$emit('close')">
                    {{ $t('message.cancel') }}
                </a-button>
                <a-button type="primary" @click="exportToClipboard" :loading="exporting">
                    <a-icon type="export" />
                    {{ $t('chatbotv2.exportToClipboard') }}
                </a-button>
            </div>
        </div>
    </a-modal>
</template>

<script>
import { mapState } from "vuex";
import { CHATBOT_API } from "../../../../../../api/chatbotv2";
import { copyText2Clipboard } from "../../../../../../utils/utils";

export default {
    name: "ExportDiagramModal",
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        storyId: {
            type: String,
            required: true,
        },
        board: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            exporting: false,
            kbCounts: {
                faq: 0,
                file: 0,
                article: 0,
                webpage: 0,
                aiFunction: 0,
            },
            aiFunctionsList: [],
            selectedFunctions: [],
            exportOptions: {
                aiSettings: false,
                aiFunctions: false,
                kb: {
                    faq: false,
                    file: false,
                    article: false,
                    webpage: false,
                },
            },
        };
    },
    computed: {
        ...mapState({
            stories: (state) => state.CB.stories,
            site: (state) => state.site.site,
        }),
        story() {
            return this.stories.find((s) => s._id === this.storyId);
        },
        storyAi() {
            return this.story && this.story.ai ? this.story.ai : {};
        },
        hasAiSettings() {
            return this.story && this.story.ai && (this.story.ai.prompt || this.story.ai.model);
        },
        totalKbCount() {
            return this.kbCounts.faq + this.kbCounts.file + this.kbCounts.article + this.kbCounts.webpage;
        },
        isAllFunctionsSelected() {
            return this.aiFunctionsList.length > 0 && this.selectedFunctions.length === this.aiFunctionsList.length;
        },
        isSomeFunctionsSelected() {
            return this.selectedFunctions.length > 0;
        },
    },
    methods: {
        async fetchData() {
            this.loading = true;
            try {
                const countRes = await CHATBOT_API.KNOWLEDGE_BASE.knowledgeBaseCountByType(this.storyId);
                this.kbCounts = countRes.data || { faq: 0, file: 0, article: 0, webpage: 0, aiFunction: 0 };

                if (this.kbCounts.aiFunction > 0) {
                    const functionsRes = await CHATBOT_API.KNOWLEDGE_BASE.listKnowledgeBase(
                        this.storyId,
                        "aiFunction",
                        1,
                        100
                    );
                    this.aiFunctionsList = (functionsRes.data.rows || []).filter((f) => f.aiFunction);
                }

                if (this.hasAiSettings) {
                    this.exportOptions.aiSettings = true;
                }
            } catch (error) {
                console.error("Failed to fetch export data:", error);
                this.$message.error(this.$t("chatbotv2.exportDataFetchError"));
            } finally {
                this.loading = false;
            }
        },
        toggleAllFunctions(e) {
            if (e.target.checked) {
                this.selectedFunctions = this.aiFunctionsList.map((f) => f._id);
            } else {
                this.selectedFunctions = [];
            }
        },
        toggleFunction(id, checked) {
            if (checked) {
                if (!this.selectedFunctions.includes(id)) {
                    this.selectedFunctions.push(id);
                }
            } else {
                this.selectedFunctions = this.selectedFunctions.filter((f) => f !== id);
            }
        },
        async exportToClipboard() {
            this.exporting = true;
            try {
                const exportData = {
                    diagram: this.board.diagram,
                    language: this.board.language || "tr",
                    exportVersion: 2,
                };

                if (this.exportOptions.aiSettings && this.hasAiSettings) {
                    exportData.aiSettings = {
                        prompt: this.storyAi.prompt,
                        model: this.storyAi.model,
                        tool_choice: this.storyAi.tool_choice,
                        temperature: this.storyAi.temperature,
                        top_p: this.storyAi.top_p,
                        enabledFunctions: this.storyAi.enabledFunctions,
                    };
                }

                if (this.exportOptions.aiFunctions && this.selectedFunctions.length > 0) {
                    const selectedFnData = this.aiFunctionsList
                        .filter((f) => this.selectedFunctions.includes(f._id))
                        .map((f) => ({
                            aiFunction: f.aiFunction,
                            enabled: f.enabled,
                        }));
                    exportData.aiFunctions = selectedFnData;
                }

                const kbTypes = [];
                if (this.exportOptions.kb.faq && this.kbCounts.faq > 0) kbTypes.push("faq");
                if (this.exportOptions.kb.file && this.kbCounts.file > 0) kbTypes.push("file");
                if (this.exportOptions.kb.article && this.kbCounts.article > 0) kbTypes.push("article");
                if (this.exportOptions.kb.webpage && this.kbCounts.webpage > 0) kbTypes.push("webpage");

                if (kbTypes.length > 0) {
                    exportData.knowledgeBaseTypes = kbTypes;
                    exportData.sourceStoryId = this.storyId;
                }

                const jsonData = JSON.stringify(exportData);
                copyText2Clipboard(jsonData);
                this.$message.success(this.$t("chatbotv2.exportSuccess"));
                this.$emit("close");
            } catch (error) {
                console.error("Export failed:", error);
                this.$message.error(this.$t("chatbotv2.exportError"));
            } finally {
                this.exporting = false;
            }
        },
    },
    watch: {
        visible: {
            handler(val) {
                if (val) {
                    this.fetchData();
                } else {
                    this.selectedFunctions = [];
                    this.exportOptions = {
                        aiSettings: false,
                        aiFunctions: false,
                        kb: {
                            faq: false,
                            file: false,
                            article: false,
                            webpage: false,
                        },
                    };
                }
            },
            immediate: true,
        },
        "exportOptions.aiFunctions": {
            handler(val) {
                if (val && this.aiFunctionsList.length > 0) {
                    this.selectedFunctions = this.aiFunctionsList.map((f) => f._id);
                } else {
                    this.selectedFunctions = [];
                }
            },
        },
    },
};
</script>

<style lang="scss">
.export-diagram-modal {
    .ant-modal-body {
        padding: 0;
    }
}
</style>

<style lang="scss" scoped>
.export-container {
    padding: 24px;

    .export-description {
        color: #516173;
        font-size: 14px;
        margin-bottom: 20px;
        padding-bottom: 16px;
        border-bottom: 1px solid #e8e8e8;
    }

    .export-section {
        background: #f9fafb;
        border-radius: 8px;
        padding: 16px;
        margin-bottom: 16px;

        .section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 8px;

            .section-title {
                font-weight: 600;
                font-size: 16px;
                color: #22272d;
                display: flex;
                align-items: center;
                gap: 8px;

                .count-badge {
                    background: #006cff;
                    color: white;
                    padding: 2px 8px;
                    border-radius: 12px;
                    font-size: 12px;
                }
            }
        }

        .section-description {
            color: #516173;
            font-size: 13px;
            margin-bottom: 12px;
        }

        .ai-settings-details {
            background: white;
            border-radius: 6px;
            padding: 12px;
            margin-top: 12px;

            .detail-item {
                display: flex;
                justify-content: space-between;
                padding: 6px 0;
                border-bottom: 1px solid #f0f0f0;

                &:last-child {
                    border-bottom: none;
                }

                .label {
                    color: #516173;
                    font-size: 13px;
                }

                .value {
                    color: #22272d;
                    font-weight: 500;
                    font-size: 13px;

                    &.truncate {
                        max-width: 300px;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }
                }
            }
        }

        .function-list {
            background: white;
            border-radius: 6px;
            padding: 12px;
            margin-top: 12px;
            max-height: 200px;
            overflow-y: auto;

            .select-all-row {
                padding-bottom: 8px;
                margin-bottom: 8px;
                border-bottom: 1px solid #f0f0f0;
                font-weight: 600;
            }

            .function-item {
                padding: 6px 0;
            }
        }

        .kb-options {
            display: flex;
            flex-direction: column;
            gap: 12px;
            margin-top: 12px;

            .kb-option {
                display: flex;
                justify-content: space-between;
                align-items: center;
                background: white;
                border-radius: 6px;
                padding: 12px;

                .kb-option-info {
                    display: flex;
                    flex-direction: column;

                    .kb-name {
                        font-weight: 600;
                        color: #22272d;
                    }

                    .kb-count {
                        font-size: 12px;
                        color: #516173;
                    }
                }
            }

            .no-kb-message {
                color: #516173;
                font-style: italic;
                text-align: center;
                padding: 12px;
            }
        }
    }

    .export-actions {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        margin-top: 24px;
        padding-top: 16px;
        border-top: 1px solid #e8e8e8;
    }
}
</style>
